/**
 * Pastille d’état. Les tables de correspondance en fin de fichier fixent, une
 * fois pour toutes, quelle couleur porte quel statut du domaine : le même
 * « Non payé » a la même allure au guichet, sur la fiche personne et dans les
 * tableaux de bord.
 */

import type { ReactNode } from 'react'

import type {
  PhaseSaison,
  ResultatScan,
  StatutBillet,
  StatutCarte,
  StatutEnvoi,
  StatutPaiement,
  StatutProposition,
  StatutRepresentation,
  StatutSouscription,
} from '@tb/shared'

import { cn } from '../lib/cn'

export type TonBadge = 'neutre' | 'info' | 'succes' | 'avertissement' | 'danger' | 'accent'

const TONS: Readonly<Record<TonBadge, string>> = {
  neutre: 'bg-slate-100 text-slate-700 ring-slate-200',
  info: 'bg-blue-50 text-blue-800 ring-blue-200',
  succes: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
  avertissement: 'bg-amber-50 text-amber-900 ring-amber-200',
  danger: 'bg-red-50 text-red-800 ring-red-200',
  accent: 'bg-rideau-50 text-rideau-800 ring-rideau-200',
}

export interface BadgeProps {
  readonly ton?: TonBadge | undefined
  /** Pastille pleine devant le texte — utile quand plusieurs badges se suivent. */
  readonly point?: boolean | undefined
  readonly className?: string | undefined
  readonly children: ReactNode
}

export function Badge({ ton = 'neutre', point = false, className, children }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
        TONS[ton],
        className,
      )}
    >
      {point && <span className="size-1.5 rounded-full bg-current" aria-hidden="true" />}
      {children}
    </span>
  )
}

/* -------------------------------------------------------------------------- */
/* Correspondances statut du domaine -> ton                                   */
/* -------------------------------------------------------------------------- */

export const TON_PHASE_SAISON: Readonly<Record<PhaseSaison, TonBadge>> = {
  preparation: 'neutre',
  interne: 'info',
  externe: 'succes',
  cloturee: 'neutre',
}

export const TON_STATUT_REPRESENTATION: Readonly<Record<StatutRepresentation, TonBadge>> = {
  programmee: 'succes',
  annulee: 'danger',
}

export const TON_STATUT_SOUSCRIPTION: Readonly<Record<StatutSouscription, TonBadge>> = {
  brouillon: 'neutre',
  confirmee: 'succes',
  annulee: 'danger',
}

export const TON_STATUT_PAIEMENT: Readonly<Record<StatutPaiement, TonBadge>> = {
  non_paye: 'avertissement',
  paye: 'succes',
}

export const TON_STATUT_BILLET: Readonly<Record<StatutBillet, TonBadge>> = {
  valide: 'succes',
  annule: 'danger',
}

export const TON_STATUT_CARTE: Readonly<Record<StatutCarte, TonBadge>> = {
  active: 'succes',
  revoquee: 'danger',
}

export const TON_STATUT_PROPOSITION: Readonly<Record<StatutProposition, TonBadge>> = {
  en_attente: 'info',
  acceptee: 'succes',
  declinee: 'neutre',
  expiree: 'avertissement',
}

export const TON_RESULTAT_SCAN: Readonly<Record<ResultatScan, TonBadge>> = {
  admis: 'succes',
  doublon: 'avertissement',
  refuse: 'danger',
  manuel: 'info',
}

export const TON_STATUT_ENVOI: Readonly<Record<StatutEnvoi, TonBadge>> = {
  envoye: 'succes',
  echec: 'danger',
}
